import { NarrativeTone } from '@/types/game';

// Helper to pick random item from array
const pick = <T>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

export interface NarrativeTexts {
  // Landing / Intro
  gameIntro: () => string;
  welcomePlayers: () => string;
  
  // Role reveal - with name jokes for day phase
  passPhone: (name: string) => string;
  youAre: () => string;
  memorizeRole: () => string;
  roleRevealDrumroll: () => string;
  pickCardJoke: (name: string) => string;
  
  // Night intro
  nightIntro: () => string;
  nightFallsText: () => string;
  
  // Cupid
  cupidAwake: () => string;
  loversChosen: () => string;
  cupidInstruction: () => string;
  
  // Werewolves
  werewolvesAwake: () => string;
  werewolvesSleep: () => string;
  werewolvesSleepNoWitch: () => string;
  werewolvesChoosing: () => string;
  
  // Witch
  witchAwake: () => string;
  witchSleep: () => string;
  witchHealOption: () => string;
  witchKillOption: () => string;
  
  // Seer
  seerAwake: () => string;
  seerResult: (name: string, isWolf: boolean) => string;
  seerInstruction: () => string;
  
  // Dawn
  dawnRises: () => string;
  dawnSuspense: () => string;
  
  // Day - someone died
  dayDeathAnnouncement: (name: string) => string;
  
  // Day - no one died
  dayNoDeathAnnouncement: () => string;
  
  // Day - personalized jokes during voting intro
  dayPlayerJoke: (name: string) => string;
  
  // Voting
  votingIntro: () => string;
  votingReminder: () => string;
  votingStart: () => string;
  votingResult: (name: string) => string;
  votingPlayerTease: (name: string) => string;
  
  // Elimination
  eliminationAnnouncement: (name: string) => string;
  eliminationWolf: (name: string) => string;
  eliminationInnocent: (name: string, role: string) => string;
  eliminationSuspense: () => string;
  
  // Hunter
  hunterRevenge: (name: string) => string;
  hunterShot: (hunterName: string, targetName: string) => string;
  hunterAiming: () => string;
  
  // Game End
  wolvesWin: () => string;
  villageWins: () => string;
  loversWin: () => string;
  gameOverIntro: () => string;
}

const TAQUIN_TEXTS: NarrativeTexts = {
  gameIntro: () => pick([
    "Alors les amis, prêts à vous entre-tuer ? Y'a des amitiés qui vont pas survivre à cette soirée, je vous préviens ! Attachez vos ceintures, ça va saigner !",
    "Bienvenue dans le jeu où vos meilleurs potes vont vous trahir ! Spoiler : ça va mal finir pour certains !",
    "C'est parti mon kiki ! Ce soir on découvre qui sont les vrais amis et qui sont les traîtres ! Âmes sensibles s'abstenir !",
    "Wesh wesh, le Loup-Garou commence ! Préparez-vous à mentir comme des arracheurs de dents !",
    "Allez les fifous, on lance la partie ! Promis, y'aura des clashs mémorables ce soir !"
  ]),
  welcomePlayers: () => pick([
    "C'est l'heure de distribuer les rôles ! Essayez de pas faire une tête de six pieds de long si vous êtes villageois, hein !",
    "Distribution des rôles ! Et pitié, arrêtez de stresser comme ça, vous transpirez déjà !",
    "On donne les cartes ! Poker face obligatoire, même si c'est pas votre fort !",
    "Allez hop, on découvre les rôles ! Que le meilleur menteur gagne !",
    "C'est l'heure de vérité ! Enfin, de mensonge plutôt vu le jeu !"
  ]),
  
  passPhone: (name: string) => pick([
    `Allez ${name}, c'est ton tour ! Les autres, fermez vos yeux de curieux, bande de fouineurs !`,
    `À toi ${name} ! Et les autres, pas de triche ou je vous mords !`,
    `${name}, approche-toi ! Le reste de la bande, on regarde ses pieds !`,
    `C'est pour ${name} ! Si quelqu'un triche, je balance tout !`,
    `Hop hop hop, ${name} au téléphone ! Les curieux vont dehors !`
  ]),
  youAre: () => pick([
    "Tadaaa ! Voici ton destin pour la soirée. Essaie de pas trop le montrer sur ta face !",
    "Roulement de tambour... Ton rôle c'est...",
    "Et le grand moment arrive ! Tu vas être...",
    "Suspense insoutenable... Tu es officiellement...",
    "Prêt ? Pas prêt ? On s'en fiche, voici ton rôle !"
  ]),
  memorizeRole: () => pick([
    "T'as intérêt à t'en souvenir, hein ! Parce que y'a pas de service après-vente !",
    "Grave ça dans ta mémoire, pas de rappel possible !",
    "Retiens bien, tu vas devoir mentir comme un pro !",
    "C'est noté ? Parfait, maintenant fais semblant de rien !",
    "Mémorise et ferme ta bouche, c'est tout ce qu'on te demande !"
  ]),
  roleRevealDrumroll: () => pick([
    "Roulement de tambour... Suspense insoutenable...",
    "La tension est à son comble...",
    "Tous les regards sont braqués sur toi...",
    "Le moment fatidique approche...",
    "Inspire un grand coup..."
  ]),
  pickCardJoke: (name: string) => pick([
    `${name}, avec ta tête de suspect, je parie déjà sur loup-garou !`,
    `Ah ${name} ! T'as l'air trop innocent pour être honnête, méfiance...`,
    `${name} qui s'approche de la carte... genre tu stresses pas du tout hein ?`,
    `Vas-y ${name}, montre-nous ta poker face de champion !`,
    `${name}, essaie de pas faire tomber le téléphone avec tes mains moites !`,
    `Oh là là ${name}, pourquoi tu transpires autant ? T'as quelque chose à te reprocher ?`,
    `${name} arrive avec son petit air mystérieux... on t'a vu !`,
    `Tiens tiens ${name}, tu fais moins le malin maintenant !`,
    `${name}, respire un coup, c'est juste un jeu... enfin presque !`,
    `Attention ${name} approche ! Que personne ne bouge !`,
    `${name} avec sa tête de premier de la classe... ça cache quelque chose !`,
    `Ah c'est ${name} ! Le suspense est insoutenable, enfin surtout pour toi !`,
    `${name}, arrête de faire cette tête, tu fais peur aux enfants !`,
    `Oulah ${name}, t'as une drôle de démarche ce soir... nerveux ?`,
    `${name} s'avance... les paris sont ouverts sur son rôle !`
  ]),
  
  nightIntro: () => pick([
    "Allez hop, dodo tout le monde ! Y'a des amitiés qui vont se briser, c'est moi qui vous l'dit !",
    "La nuit tombe ! Fermez vos yeux et priez pour survivre !",
    "Bonne nuit les petits... enfin, bonne nuit à ceux qui vont survivre !",
    "C'est l'heure de dormir ! Ou de manger des villageois pour certains...",
    "Nuit nuit ! Faites de beaux rêves... ou de beaux cauchemars !"
  ]),
  nightFallsText: () => pick([
    "La nuit tombe sur ce village de fifous. Fermez les yeux, et surtout, fermez vos bouches !",
    "L'obscurité envahit le village. Silence, les loups rôdent...",
    "La lune se lève sur le village endormi... enfin presque endormi !",
    "Chut ! La nuit est là, les créatures sortent de l'ombre !",
    "Le village s'endort... mais tout le monde ne dormira pas !"
  ]),
  
  cupidAwake: () => pick([
    "Cupidon, réveille-toi et joue les entremetteurs ! Choisis deux pigeons qui vont s'aimer à en crever... littéralement.",
    "Cupidon ! C'est l'heure de faire des ravages amoureux !",
    "Oh Cupidon ! Désigne deux tourtereaux pour qu'ils vivent une histoire tragique !",
    "Cupidon, à toi de jouer les Tinder version médiévale !",
    "L'amour appelle ! Cupidon, trouve-nous un couple maudit !"
  ]),
  loversChosen: () => pick([
    "Oooh c'est mignon, ils sont amoureux ! Profitez-en bien parce que ça risque de pas durer ! Les loups, à vous !",
    "Deux coeurs unis ! C'est beau... enfin jusqu'à ce que l'un d'eux se fasse croquer !",
    "L'amour est dans le pré ! Enfin, dans le village rempli de loups !",
    "Cupidon a frappé ! Deux amoureux transis ! C'est meugnon !",
    "Et voilà, deux pigeons sont amoureux ! Croisons les doigts pour eux !"
  ]),
  cupidInstruction: () => pick([
    "Alors Cupidon, t'as trouvé tes deux victimes de l'amour ?",
    "Cupidon, montre-nous tes talents de matchmaker !",
    "Qui va tomber amoureux ce soir ? C'est toi qui décides !",
    "Cupidon, fais chauffer ton arc !",
    "À qui vas-tu briser le coeur ce soir, Cupidon ?"
  ]),
  
  werewolvesAwake: () => pick([
    "Les gros vilains loups ouvrent les yeux ! Alors, qui va se faire croquer ce soir ? Faites pas genre vous hésitez !",
    "Loups-garous, debout ! C'est l'heure du casse-croûte !",
    "Les loups sortent de leur tanière ! Qui sera le dîner de ce soir ?",
    "Réveillez-vous les loups ! La chasse est ouverte !",
    "Les loups affamés ouvrent les yeux ! Le buffet est ouvert !"
  ]),
  werewolvesSleep: () => pick([
    "Les loups peuvent se rendormir, bien repus les coquins ! Sorcière, debout, on a besoin de tes potions de mamie !",
    "Bonne nuit les loups, le ventre plein ! Sorcière, à toi !",
    "Les loups retournent se coucher ! Place à la magie !",
    "Dodo les toutous ! Sorcière, c'est ton heure de gloire !",
    "Les loups ont fait leur choix ! Sorcière, réveille-toi !"
  ]),
  werewolvesSleepNoWitch: () => pick([
    "Bonne nuit les loups ! Voyante, c'est l'heure de matter dans la vie privée des gens !",
    "Les loups se rendorment ! Voyante, ouvre l'oeil !",
    "Dodo les carnassiers ! Place à la voyante !",
    "Les loups ont fini leur boulot ! Voyante, en scène !",
    "Nuit les loups ! Voyante, c'est à toi de jouer les commères !"
  ]),
  werewolvesChoosing: () => pick([
    "Alors les loups, vous vous décidez ou vous comptez les moutons ?",
    "Les loups, vous avez fait votre choix ? Pressez-vous !",
    "Décision décision ! C'est long les loups !",
    "Alors les carnassiers, c'est pour aujourd'hui ou pour demain ?",
    "Les loups, mettez-vous d'accord, le village attend pas !"
  ]),
  
  witchAwake: () => pick([
    "Sorcière, réveille-toi ! C'est l'heure de jouer avec tes petites potions !",
    "Sorcière ! Debout et sors ton chaudron !",
    "La sorcière ouvre les yeux ! Qu'est-ce qu'on fait ce soir ?",
    "Sorcière, c'est ton moment ! Sauve ou tue, à toi de choisir !",
    "Réveil de la sorcière ! Les potions sont prêtes ?"
  ]),
  witchSleep: () => pick([
    "La sorcière range ses fioles et va pioncer. Voyante, à toi de jouer les commères !",
    "Merci sorcière ! Rendors-toi. Voyante, debout !",
    "Sorcière au dodo ! Place à la voyante !",
    "La sorcière a fini ses magouilles ! Voyante, c'est ton tour !",
    "Bonne nuit la sorcière ! Voyante, ouvre les yeux !"
  ]),
  witchHealOption: () => pick([
    "Tu veux sauver ce pauvre malheureux ? T'as le coeur tendre dis donc !",
    "La potion de vie te fait de l'oeil ?",
    "Un geste héroïque ? Sauver cette victime ?",
    "Tu joues les héros ce soir ?",
    "Cette âme mérite-t-elle d'être sauvée ?"
  ]),
  witchKillOption: () => pick([
    "Ou alors tu veux buter quelqu'un ? C'est Noël avant l'heure !",
    "La potion de mort te démange ?",
    "Tu veux régler des comptes cette nuit ?",
    "Envie de faire le ménage dans le village ?",
    "La vengeance est un plat qui se boit froid ?"
  ]),
  
  seerAwake: () => pick([
    "Voyante, ouvre tes mirettes et va fouiner dans les secrets des autres !",
    "Voyante ! C'est l'heure de mater les voisins !",
    "La voyante se réveille ! Qui vas-tu stalker ce soir ?",
    "Voyante, à toi de percer les mystères de la nuit !",
    "Les yeux de la voyante s'ouvrent ! Qui sera démasqué ?"
  ]),
  seerResult: (name: string, isWolf: boolean) => isWolf 
    ? pick([
      `Bingo ! ${name} c'est un loup ! Essaie de pas le crier sur tous les toits, subtilité toussa... Mais on sait tous que tu vas le balancer !`,
      `ALERTE ! ${name} est un loup-garou ! Garde ça pour toi... ou pas !`,
      `Oh oh ! ${name} cache une fourrure sous ses vêtements ! C'est un loup !`,
      `${name} ? LOUP ! Maintenant fais pas de bêtises avec cette info !`,
      `Jackpot ! ${name} c'est du 100% loup ! Savoure cette info !`
    ])
    : pick([
      `${name} est clean ! Enfin, il est pas loup quoi, pour le reste on sait pas... Il cache peut-être d'autres trucs !`,
      `${name} c'est un gentil villageois ! Enfin, gentil c'est relatif...`,
      `Pas de poils de loup chez ${name} ! C'est un innocent !`,
      `${name} est du bon côté ! Enfin probablement...`,
      `${name} ? Pas de danger de ce côté-là ! C'est du village pur jus !`
    ]),
  seerInstruction: () => pick([
    "Alors, sur qui tu veux balancer ta boule de cristal ?",
    "Qui mérite d'être démasqué ce soir ?",
    "Désigne ta cible pour le flash info !",
    "Sur qui veux-tu faire ta petite enquête ?",
    "Pointe du doigt celui que tu veux scanner !"
  ]),
  
  dawnRises: () => pick([
    "Debout là-dedans ! C'est l'heure de découvrir les dégâts de cette nuit de folie !",
    "Le soleil se lève ! L'heure du bilan a sonné !",
    "Réveil général ! On compte les survivants !",
    "Cocorico ! C'est le matin et le village se réveille !",
    "L'aube pointe son nez ! Qui a survécu à cette nuit ?"
  ]),
  dawnSuspense: () => pick([
    "Le soleil se lève... Suspense... Qui a passé une mauvaise nuit ?",
    "La lumière revient... et avec elle, les mauvaises nouvelles ?",
    "Le village s'éveille doucement... trop tranquillement ?",
    "Les premiers rayons illuminent le village...",
    "L'heure de vérité approche..."
  ]),
  
  dayDeathAnnouncement: (name: string) => pick([
    `Mauvaise nouvelle les amis ! ${name} s'est fait grignoter cette nuit. Fallait pas dormir aussi profondément ! Les loups lui ont fait sa fête !`,
    `RIP ${name} ! Les loups avaient faim et il était au menu ! Paix à son âme !`,
    `${name} a pas survécu à la nuit ! Les loups l'ont croqué comme un petit biscuit !`,
    `Tragédie ! ${name} n'est plus ! Les loups ont encore frappé !`,
    `${name} nous a quittés cette nuit... mangé par les loups ! Quelle triste fin !`
  ]),
  dayNoDeathAnnouncement: () => pick([
    "Incroyable ! Personne n'est mort ! Les loups ont dû se goinfrer avant, ou alors ils sont juste nuls. Bravo les losers !",
    "Miracle ! Tout le monde est vivant ! Les loups ont raté leur coup !",
    "Surprise ! Aucun mort cette nuit ! Les loups étaient en grève ?",
    "Le village est intact ! Soit les loups sont nuls, soit y'a eu intervention divine !",
    "Personne n'est mort ! C'est Noël ou quoi ?"
  ]),
  dayPlayerJoke: (name: string) => pick([
    `${name}, t'as une tête de quelqu'un qui a mal dormi... ou qui a mangé du villageois !`,
    `Regardez ${name}, il évite les regards... suspect ou juste timide ?`,
    `${name} qui fait semblant de rien... classique !`,
    `Ah ${name}, toujours vivant ? Chanceux ou complice des loups ?`,
    `${name}, cette nuit t'as fait quoi exactement ? Hmmm ?`,
    `${name} a l'air bien reposé... trop reposé peut-être ?`,
    `Tiens ${name} qui sourit... c'est louche ça !`,
    `${name}, pourquoi tu nous fixes comme ça ?`,
    `Oh ${name} ! T'as pas l'air dans ton assiette ce matin !`,
    `${name}, t'as quelque chose à nous dire ? Genre, une confession ?`,
    `${name} qui fait l'innocent... on te voit va !`,
    `Eh ${name} ! C'est quoi ce petit sourire en coin ?`,
    `${name}, t'as pas fermé l'oeil de la nuit ou quoi ?`,
    `Regardez-moi ${name}... il transpire déjà !`,
    `${name} avec sa poker face de champion... ou de coupable ?`
  ]),
  
  votingIntro: () => pick([
    "C'est l'heure de balancer vos potes ! Qui c'est le loup ? Allez, dénoncez, c'est pour la bonne cause !",
    "Place au vote ! Sortez vos plus beaux arguments et accusations !",
    "Le tribunal populaire est ouvert ! Accusez-vous les uns les autres !",
    "C'est l'heure de la délation ! Que le moins suspect gagne !",
    "Votes votes votes ! Trouvons ce satané loup !"
  ]),
  votingReminder: () => pick([
    "N'oubliez pas : le plus bruyant est pas toujours le plus innocent... ou si ? C'est toi le loup en fait ?",
    "Regardez bien autour de vous... le loup se cache parmi vous !",
    "Méfiez-vous des apparences ! L'innocent peut être coupable !",
    "Le loup est malin, ne vous faites pas avoir !",
    "Observez, analysez, et surtout... accusez !"
  ]),
  votingStart: () => pick([
    "À vos marques, prêts, balancez !",
    "C'est parti pour les accusations !",
    "Que le vote commence !",
    "Lâchez les chiens... euh les votes !",
    "Fire in the hole ! On vote !"
  ]),
  votingResult: (name: string) => pick([
    `Le verdict est tombé ! ${name} a été choisi pour le grand départ !`,
    `C'est décidé ! ${name} est éliminé par le vote populaire !`,
    `${name} fait ses valises ! Le village a tranché !`,
    `RIP ${name} ! La démocratie a parlé !`,
    `${name} est désigné ! Que justice soit faite !`
  ]),
  votingPlayerTease: (name: string) => pick([
    `${name} qui vote... j'espère que t'as réfléchi avec ton cerveau cette fois !`,
    `Ah ${name} pointe du doigt ! Accusateur ou manipulateur ?`,
    `${name}, tu votes pour qui ? Ton meilleur pote j'espère !`,
    `Le vote de ${name}... toujours aussi prévisible !`,
    `${name} fait son choix ! C'est stratégique ou impulsif ?`,
    `Oh ${name} désigne quelqu'un ! Ça promet des étincelles !`,
    `${name} accuse ! Les tensions montent !`,
    `${name}, ce doigt accusateur... t'assumes ?`
  ]),
  
  eliminationAnnouncement: (name: string) => pick([
    `Et bam ! ${name} se fait virer du village ! C'était p'têtre un loup, ou p'têtre un innocent... on verra bien !`,
    `${name} prend la porte ! Le village a décidé !`,
    `Bye bye ${name} ! Le peuple a parlé !`,
    `${name} est éliminé ! Justice ou erreur judiciaire ?`,
    `${name} quitte l'aventure ! Moment de vérité !`
  ]),
  eliminationWolf: (name: string) => pick([
    `Bien joué bande de malins ! ${name} c'était vraiment un loup ! Pour une fois que vous visez juste ! Champagne !`,
    `YESSS ! ${name} était un loup ! Bravo le village !`,
    `${name} était effectivement un loup ! Vous êtes pas si nuls finalement !`,
    `Victoire ! ${name} c'était du 100% loup-garou ! Bien joué !`,
    `${name} grillé ! C'était bien un loup ! Le village marque un point !`
  ]),
  eliminationInnocent: (name: string, role: string) => pick([
    `Oups... ${name} était ${role}. Bravo les génies, vous venez de buter un allié ! La prochaine fois réfléchissez !`,
    `Aïe aïe aïe ! ${name} c'était ${role} ! Vous avez tué un innocent !`,
    `Catastrophe ! ${name} était ${role} ! Erreur fatale !`,
    `${name} était innocent ! ${role} pour être précis ! Bravo les boulets !`,
    `Oups la boulette ! ${name} était ${role} et vous l'avez buté ! GG !`
  ]),
  eliminationSuspense: () => pick([
    "Alors, c'était quoi son rôle ? Roulement de tambour...",
    "Moment de vérité ! C'était un loup ou pas ?",
    "Suspense insoutenable... la révélation approche !",
    "On retourne la carte... et le rôle était...",
    "Attention les yeux ! On découvre son vrai visage !"
  ]),
  
  hunterRevenge: (name: string) => pick([
    `Oh oh ! ${name} c'était le Chasseur ! Et il est pas content content... qui va prendre une balle ? Suspense !`,
    `ALERTE ! ${name} est le Chasseur ! Il dégaine son arme !`,
    `${name} révèle son arc ! C'est le Chasseur et il veut sa revanche !`,
    `Surprise ! ${name} était Chasseur ! Quelqu'un va morfler !`,
    `${name} le Chasseur ne partira pas seul ! Qui vise-t-il ?`
  ]),
  hunterShot: (hunterName: string, targetName: string) => pick([
    `PAN ! ${hunterName} a descendu ${targetName}. Fallait pas l'énerver celui-là ! Deux pour le prix d'un !`,
    `BANG ! ${hunterName} emporte ${targetName} dans sa chute ! Drama !`,
    `${hunterName} tire sur ${targetName} ! Double élimination !`,
    `Headshot ! ${hunterName} a eu ${targetName} ! Vengeance accomplie !`,
    `${hunterName} ne part pas seul ! ${targetName} l'accompagne !`
  ]),
  hunterAiming: () => pick([
    "Le chasseur lève son arme... Il vise... C'est tendu !",
    "L'arc est bandé... la tension est palpable !",
    "Le chasseur cherche sa cible... Qui sera le malheureux ?",
    "Un dernier tir... Le chasseur prend son temps...",
    "Dans le viseur du chasseur... Qui va y passer ?"
  ]),
  
  wolvesWin: () => pick([
    "Les loups ont gagné ! Franchement, vous l'avez bien mérité cette défaite, bande de nazes ! Le village est dévoré !",
    "Game over ! Les loups règnent sur le village ! Défaite totale !",
    "Les loups triomphent ! Le village est anéanti ! GG les carnassiers !",
    "Fin de partie ! Les loups ont tout bouffé ! Bravo... enfin pas pour les villageois !",
    "Victoire des loups ! Le village n'est plus ! C'est la débandade !"
  ]),
  villageWins: () => pick([
    "Le village a gagné ! Miraculeux vu comment c'était mal parti ! Les loups se sont fait avoir comme des bleus !",
    "VICTOIRE ! Le village a triomphé des loups ! Champagne !",
    "Les villageois gagnent ! Les loups sont exterminés ! Bravo !",
    "Game over les loups ! Le village reste debout ! Belle perf !",
    "Le village survit ! Les loups sont tous morts ! C'est la fête !"
  ]),
  loversWin: () => pick([
    "Les amoureux gagnent ! L'amour triomphe de tout, même de votre incompétence collective ! C'est beau l'amour !",
    "Love wins ! Les amoureux remportent la partie ! Trop mignon !",
    "Victoire de l'amour ! Les tourtereaux survivent ! C'est romantique !",
    "Les lovers gagnent ! Contre toute attente, ils sont ensemble jusqu'au bout !",
    "L'amour a vaincu ! Les amoureux triomphent ! Happy ending !"
  ]),
  gameOverIntro: () => pick([
    "C'est fini les amis ! On fait les comptes de cette soirée de folie !",
    "Et c'est terminé ! Quelle partie mes amis !",
    "Fin de partie ! Le rideau tombe sur ce drame villageois !",
    "Game over ! C'était intense les amis !",
    "La partie s'achève ! Quel suspense !"
  ]),
};

const COQUIN_TEXTS: NarrativeTexts = {
  gameIntro: () => pick([
    "Bienvenue dans une nuit torride au village. Les loups ont faim... et pas que de viande si vous voyez ce que je veux dire. Ça va chauffer sous les draps !",
    "Bonsoir les coquinous ! Ce soir, le village va vibrer de passion... et de terreur !",
    "Bienvenue dans le Loup-Garou version hot ! Attention, ça va chauffer !",
    "Ohhhh que cette nuit s'annonce... palpitante ! Le village va connaître des frissons !",
    "Bienvenue dans cette partie sulfureuse ! Les loups sont en chasse... et pas que pour manger !"
  ]),
  welcomePlayers: () => pick([
    "Les rôles vont être distribués. Essayez de garder votre poker face, même si vous êtes excités comme des puces !",
    "On distribue les cartes sexy ! Gardez votre sang-froid... si vous pouvez !",
    "C'est l'heure de découvrir vos désirs secrets... enfin vos rôles !",
    "Distribution des rôles ! Que la tension monte doucement...",
    "Les destins se dévoilent ! Essayez de contenir votre excitation !"
  ]),
  
  passPhone: (name: string) => pick([
    `C'est au tour de ${name} de découvrir son petit secret coquin. Les autres, pas de voyeurisme !`,
    `${name}, approche sensuellement... c'est ton moment !`,
    `Oh ${name}, c'est à toi de jouer les mystérieux !`,
    `${name}, viens découvrir ton destin... les yeux dans les yeux !`,
    `À toi ${name} ! Les autres, un peu d'intimité s'il vous plaît !`
  ]),
  youAre: () => pick([
    "Découvre ce que le destin te réserve cette nuit chaude...",
    "Le voile se lève sur ton rôle secret...",
    "Prépare-toi à une révélation... sensationnelle !",
    "Ton destin s'offre à toi...",
    "La vérité sur ton identité secrète..."
  ]),
  memorizeRole: () => pick([
    "Grave ça dans ta mémoire, ça va être une looongue nuit torride.",
    "Retiens bien, les plaisirs de la nuit t'attendent !",
    "Mémorise ton rôle... et prépare-toi à jouer !",
    "Cette info, c'est ton atout séduction pour la soirée !",
    "Garde ce secret bien au chaud..."
  ]),
  roleRevealDrumroll: () => pick([
    "Suspense érotique... Qu'est-ce qui se cache sous cette carte ?",
    "Le désir monte... la révélation approche...",
    "Tension maximale...",
    "Les coeurs s'emballent...",
    "Le moment tant attendu..."
  ]),
  pickCardJoke: (name: string) => pick([
    `${name}, t'as les mains qui tremblent ou c'est l'excitation ?`,
    `Oh ${name}, avec ce regard charmeur, tu caches quelque chose...`,
    `${name} qui s'approche sensuellement de la carte... ça promet !`,
    `Vas-y ${name}, montre-nous ce que tu caches sous cette chemise... euh carte !`,
    `${name}, t'as l'air d'avoir chaud... ça va aller ?`,
    `${name} arrive avec son regard de braise... attention les coeurs !`,
    `Oh là ${name}, cette démarche féline... tu nous prépares quoi ?`,
    `${name}, pourquoi tu rougis déjà ? On a même pas commencé !`,
    `${name} s'approche... le charme opère déjà !`,
    `Tiens tiens ${name}, t'as mis ton parfum de séduction ce soir ?`,
    `${name} avec son petit air mystérieux... craquant !`,
    `${name}, arrête de nous faire languir et prends ta carte !`,
    `Oh ${name}, ce regard... tu nous caches des choses coquines ?`,
    `${name} qui fait monter la température... on attend avec impatience !`,
    `${name}, respire... la tension est palpable !`
  ]),
  
  nightIntro: () => pick([
    "La nuit tombe... Fermez les yeux et gardez vos mains où je peux les voir, bande de coquins !",
    "L'obscurité s'invite... Les désirs inavoués peuvent s'exprimer !",
    "La nuit enveloppe le village de son voile mystérieux...",
    "Chhhut... la nuit arrive, chaude et mystérieuse...",
    "Les lumières s'éteignent... place aux secrets de la nuit !"
  ]),
  nightFallsText: () => pick([
    "L'obscurité s'installe... C'est l'heure des petits secrets inavouables !",
    "Le village s'endort... mais pas tout le monde !",
    "La lune éclaire les désirs cachés...",
    "Dans l'ombre, les passions s'éveillent...",
    "La nuit, tous les chats sont gris... et les loups sont affamés !"
  ]),
  
  cupidAwake: () => pick([
    "Cupidon, montre-nous tes talents d'entremetteur ! Qui va finir dans le lit de qui cette nuit ?",
    "Oh Cupidon ! L'heure des coeurs battants a sonné !",
    "Cupidon chéri, trouve-nous un couple passion !",
    "L'amour appelle ! Cupidon, fais des miracles !",
    "Cupidon, joue les Tinder version romantique !"
  ]),
  loversChosen: () => pick([
    "Oh là là, ces deux-là vont pas pouvoir se quitter d'une semelle ! Y'a de l'électricité dans l'air ! Les loups, à vous !",
    "L'amour a frappé ! Ces deux-là sont liés pour le meilleur et pour le pire !",
    "Deux coeurs battent à l'unisson ! C'est beau !",
    "Les amoureux sont désignés ! Que la passion commence !",
    "Cupidon a fait mouche ! L'amour fleurit dans ce village !"
  ]),
  cupidInstruction: () => pick([
    "Alors Cupidon, tu nous fais un petit match making torride ?",
    "Cupidon, à toi de créer la magie !",
    "Qui mérite de connaître l'amour ce soir ?",
    "Désigne les deux élus de ton coeur !",
    "Cupidon, fais chauffer ton arc !"
  ]),
  
  werewolvesAwake: () => pick([
    "Les loups ont faim... et pas que de viande si vous voyez ce que je veux dire ! Choisissez votre proie !",
    "Les loups ouvrent leurs yeux affamés ! La chasse est ouverte !",
    "Réveil des prédateurs ! Qui sera le dessert ce soir ?",
    "Les loups sortent jouer ! Qui va se faire croquer ?",
    "L'appétit des loups se réveille ! Menu du jour ?"
  ]),
  werewolvesSleep: () => pick([
    "Les loups ont bien bouffé cette nuit ! Sorcière, ramène tes potions magiques et tes charmes !",
    "Repus, les loups se rendorment ! Place à la magie !",
    "Les loups satisfaits vont dormir ! Sorcière, à toi !",
    "Bonne nuit les carnassiers ! La sorcière entre en scène !",
    "Les loups ont fait leur festin ! Sorcière, debout !"
  ]),
  werewolvesSleepNoWitch: () => pick([
    "Bonne nuit les loups, bien repus ! Voyante, viens mater ce qui se passe dans les ténèbres !",
    "Les loups s'endorment ! Voyante, ouvre les yeux !",
    "Dodo les loups ! Place aux visions de la voyante !",
    "Les loups sont satisfaits ! Voyante, c'est ton show !",
    "Nuit les prédateurs ! Voyante, entre en scène !"
  ]),
  werewolvesChoosing: () => pick([
    "Alors les loups, vous voulez croquer qui cette nuit ?",
    "Décision des loups ! Qui sera l'heureux élu ?",
    "Les loups délibèrent... le suspense est insoutenable !",
    "Qui attire votre appétit ce soir, les loups ?",
    "La meute se concerte... quel sera le choix ?"
  ]),
  
  witchAwake: () => pick([
    "Sorcière sexy, réveille-toi ! C'est l'heure de jouer avec tes potions aphrodisiaques !",
    "La sorcière ouvre ses yeux mystérieux !",
    "Sorcière, debout ! Tes potions t'attendent !",
    "Réveil de notre enchanteresse préférée !",
    "La sorcière entre en scène avec ses charmes !"
  ]),
  witchSleep: () => pick([
    "La sorcière a fait ses petites affaires. Voyante, à toi de sonder les âmes... et peut-être autre chose !",
    "La sorcière se rendort ! Place à la voyante !",
    "Merci sorcière ! Voyante, c'est ton moment !",
    "La sorcière range ses potions ! Voyante, ouvre les yeux !",
    "Dodo la sorcière ! La voyante prend le relais !"
  ]),
  witchHealOption: () => pick([
    "Tu veux sauver cette pauvre victime ? C'est pas très coquin ça !",
    "Un geste héroïque ce soir ?",
    "La potion de vie te tente ?",
    "Tu joues les anges gardiens ?",
    "Un coeur tendre ce soir ?"
  ]),
  witchKillOption: () => pick([
    "Ou tu préfères envoyer quelqu'un au septième ciel... définitivement ?",
    "Envie de vengeance passionnelle ?",
    "La potion mortelle te fait de l'oeil ?",
    "Un petit meurtre entre amis ?",
    "Tu veux régler des comptes cette nuit ?"
  ]),
  
  seerAwake: () => pick([
    "Voyante, ouvre grand les yeux et regarde ce que cachent vraiment les villageois sous leur couverture !",
    "La voyante se réveille ! Que vois-tu dans ta boule de cristal ?",
    "Voyante mystérieuse, c'est l'heure des révélations !",
    "Les yeux de la voyante s'illuminent !",
    "Voyante, dévoile-nous les secrets de la nuit !"
  ]),
  seerResult: (name: string, isWolf: boolean) => isWolf 
    ? pick([
      `Ouh la... ${name} cache un loup dans son pantalon ! Enfin, c'EST un loup quoi. Et pas qu'un peu !`,
      `${name} est un loup ! Sexy mais dangereux !`,
      `Alerte ! ${name} a des instincts... sauvages ! C'est un loup !`,
      `${name} cache bien son jeu... c'est un loup-garou !`,
      `${name} ? Loup ! Séduisant mais mortel !`
    ])
    : pick([
      `${name} est innocent... enfin côté loup-garou. Pour le reste, on sait pas ce qu'il fait de ses nuits coquines.`,
      `${name} n'est pas un loup ! Un gentil villageois... enfin, gentil...`,
      `Pas de danger chez ${name} ! Du moins pas de crocs !`,
      `${name} est clean ! Enfin, pas loup quoi !`,
      `${name} c'est du village pur jus ! Innocent... ou presque !`
    ]),
  seerInstruction: () => pick([
    "Alors voyante, dans les draps de qui tu veux mater ?",
    "Qui mérite ton attention cette nuit ?",
    "Désigne ta cible, voyante curieuse !",
    "Sur qui veux-tu lever le voile ?",
    "Qui attire ta curiosité ce soir ?"
  ]),
  
  dawnRises: () => pick([
    "Le soleil se lève sur des corps en sueur... de peur bien sûr ! Voyons qui a survécu aux festivités nocturnes !",
    "L'aube pointe son nez ! Réveil sensuel pour le village !",
    "Le jour se lève ! Qui a passé une nuit agitée ?",
    "Réveil les dormeurs ! L'heure du bilan a sonné !",
    "Le soleil caresse le village... qui est encore là ?"
  ]),
  dawnSuspense: () => pick([
    "L'aube se pointe... Qui a passé une nuit agitée ?",
    "Le village s'éveille doucement...",
    "Les premiers rayons percent la brume...",
    "Le suspense de l'aurore...",
    "Que nous réserve ce nouveau jour ?"
  ]),
  
  dayDeathAnnouncement: (name: string) => pick([
    `${name} a passé une très mauvaise nuit... les loups l'ont complètement dévoré ! Il en reste plus un morceau ! Quelle façon de partir !`,
    `RIP ${name} ! Une nuit fatale pour notre ami !`,
    `${name} nous a quittés ! Les loups ont été... passionnés !`,
    `Tragédie ! ${name} n'a pas survécu aux ardeurs de la nuit !`,
    `${name} s'est fait croquer ! Quelle fin dramatique !`
  ]),
  dayNoDeathAnnouncement: () => pick([
    "Personne n'est mort ! Les loups devaient être occupés à autre chose cette nuit... Des cochonneries sûrement !",
    "Miracle ! Tout le monde est vivant ! Les loups étaient distraits ?",
    "Aucun mort ! Les loups ont perdu leur appétit ?",
    "Surprise ! Le village est intact ce matin !",
    "Personne n'a été croqué ! Nuit tranquille !"
  ]),
  dayPlayerJoke: (name: string) => pick([
    `${name} a l'air fatigué... nuit agitée hein ?`,
    `Regardez ${name}, il rougit ! Qu'est-ce que t'as fait cette nuit ?`,
    `${name} qui évite les regards... tu caches des choses coquines ?`,
    `Oh ${name}, cette tête de lendemain de soirée... raconte !`,
    `${name}, t'as bien dormi ou t'as fait des folies ?`,
    `${name} avec ce petit sourire... qu'est-ce que tu nous caches ?`,
    `Tiens ${name}, t'as l'air bien réveillé toi... trop réveillé ?`,
    `${name}, pourquoi ce regard fuyant ?`,
    `${name} qui fait l'innocent... on te voit !`,
    `Oh ${name}, cette démarche... nuit mouvementée ?`,
    `${name}, t'as des cernes ou c'est le maquillage ?`,
    `${name} rougit déjà ! Coupable ?`,
    `${name}, pourquoi tu souris comme ça ?`,
    `${name} a l'air... comblé ? Suspect !`,
    `${name}, cette nuit t'a marqué on dirait !`
  ]),
  
  votingIntro: () => pick([
    "C'est l'heure de régler vos comptes ! Qui va se faire lyncher aujourd'hui ? Votez avec passion !",
    "Place au vote passionnel ! Accusez avec ferveur !",
    "L'heure du jugement a sonné ! Que les tensions éclatent !",
    "Votes ! Trahisons ! Drama ! C'est parti !",
    "Le village doit choisir ! Que la passion guide vos votes !"
  ]),
  votingReminder: () => pick([
    "Regardez-vous dans les yeux... le loup est parmi vous, et il vous a peut-être déjà séduit avec ses charmes !",
    "Méfiez-vous des apparences séduisantes !",
    "Le loup a peut-être un regard charmeur...",
    "Ne vous laissez pas aveugler par le charme !",
    "L'ennemi est peut-être le plus séduisant d'entre vous !"
  ]),
  votingStart: () => pick([
    "C'est l'heure de la punition ! Votez !",
    "Que le lynchage commence !",
    "À vos votes, prêts, accusez !",
    "Le tribunal de l'amour... euh du village est ouvert !",
    "Votez avec passion !"
  ]),
  votingResult: (name: string) => pick([
    `${name} va passer un mauvais quart d'heure ! Le village a choisi sa victime !`,
    `${name} est désigné ! Le village a tranché !`,
    `C'est ${name} qui part ! Décision finale !`,
    `${name} fait ses adieux ! Le peuple a parlé !`,
    `${name} est éliminé ! Bye bye !`
  ]),
  votingPlayerTease: (name: string) => pick([
    `${name} vote avec passion ! C'est chaud !`,
    `Oh ${name} désigne quelqu'un... c'est tendu !`,
    `${name}, ce doigt accusateur... sexy !`,
    `Le choix de ${name}... ça promet !`,
    `${name} fait son choix ! Intense !`,
    `${name} accuse ! Le drama commence !`,
    `${name} montre du doigt ! Qui est visé ?`,
    `Le vote sensuel de ${name}... intrigant !`
  ]),
  
  eliminationAnnouncement: (name: string) => pick([
    `${name} va prendre cher ! Le village en a décidé ainsi, et c'est pas du fake !`,
    `${name} quitte le village ! Décision finale !`,
    `Bye ${name} ! Le village t'a choisi !`,
    `${name} est éliminé ! Adieu !`,
    `${name} prend la porte ! Fin de l'aventure !`
  ]),
  eliminationWolf: (name: string) => pick([
    `${name} était un loup ! Bien joué, il avait pourtant l'air si... innocent et séduisant.`,
    `LOUP ! ${name} était un prédateur ! Bien vu !`,
    `${name} grillé ! C'était bien un loup !`,
    `Bravo ! ${name} était effectivement un loup !`,
    `${name} le loup est démasqué ! Victoire !`
  ]),
  eliminationInnocent: (name: string, role: string) => pick([
    `Merde... ${name} était ${role}. Vous venez de vous faire avoir comme des bleus en chaleur.`,
    `Oups ! ${name} était innocent ! C'était ${role} !`,
    `Catastrophe ! ${name} était ${role} ! Erreur fatale !`,
    `Aïe ! ${name} était ${role} ! Mauvais choix !`,
    `${name} était ${role} ! Vous avez tué un allié !`
  ]),
  eliminationSuspense: () => pick([
    "Alors, c'était un loup sexy ou un innocent séduisant ?",
    "Révélation ! C'était quoi son rôle ?",
    "Le moment de vérité...",
    "Suspense... le rôle était...",
    "On découvre la vérité !"
  ]),
  
  hunterRevenge: (name: string) => pick([
    `${name} sort son arme ! Oh là là, c'est le Chasseur et il est chaud bouillant ! Qui va prendre ?`,
    `Surprise ! ${name} est le Chasseur ! Revanche imminente !`,
    `${name} dégaine ! C'est le Chasseur furieux !`,
    `Le Chasseur ${name} veut sa vengeance !`,
    `${name} révèle son arc ! Qui va morfler ?`
  ]),
  hunterShot: (hunterName: string, targetName: string) => pick([
    `BANG ! ${hunterName} a tiré sur ${targetName}. C'était sa dernière gâchette ! Quelle fin explosive !`,
    `${hunterName} emporte ${targetName} ! Double drama !`,
    `Tir fatal ! ${hunterName} touche ${targetName} !`,
    `${hunterName} fait feu sur ${targetName} ! Spectaculaire !`,
    `Vengeance accomplie ! ${hunterName} a eu ${targetName} !`
  ]),
  hunterAiming: () => pick([
    "Le chasseur bande son arc... C'est tendu !",
    "Le chasseur vise... Le suspense est à son comble !",
    "Dans la ligne de mire...",
    "Le dernier tir se prépare...",
    "Qui sera la cible finale ?"
  ]),
  
  wolvesWin: () => pick([
    "Les loups ont gagné ! Ils ont dévoré tout le village. Quelle orgie de victoire !",
    "Victoire des loups ! Le village est anéanti !",
    "Game over ! Les loups triomphent !",
    "Les prédateurs gagnent ! Défaite totale du village !",
    "Les loups règnent ! Plus de villageois !"
  ]),
  villageWins: () => pick([
    "Le village triomphe ! Les loups sont tous morts. C'est la fête au village ce soir, et ça va chauffer !",
    "Victoire ! Le village survit !",
    "Les villageois gagnent ! Champagne !",
    "Les loups sont exterminés ! Célébration !",
    "Le village l'emporte ! Quelle soirée !"
  ]),
  loversWin: () => pick([
    "Les amoureux gagnent ! Leur passion a survécu à tout. Ils vont pouvoir célébrer ça comme il se doit...",
    "L'amour triomphe ! Les lovers gagnent !",
    "Victoire des amoureux ! C'est romantique !",
    "L'amour vainqueur ! Happy ending !",
    "Les tourtereaux survivent ! Belle histoire !"
  ]),
  gameOverIntro: () => pick([
    "C'est fini ! Quelle nuit torride on a passé ensemble !",
    "Fin de partie ! Quelle aventure !",
    "Game over ! C'était chaud !",
    "La partie s'achève ! Mémorable !",
    "C'est terminé ! Quelle soirée !"
  ]),
};

const ARABE_TEXTS: NarrativeTexts = {
  gameIntro: () => pick([
    "Wallah bienvenue dans le Loup-Garou frère ! Ce soir on va voir qui sont les vrais et qui sont les traîtres par Allah ! Ça va être chaud patate !",
    "Wesh wesh les frères ! C'est parti pour le Loup-Garou version wesh ! Inchallah on va kiffer !",
    "Bismillah on commence ! Le Loup-Garou c'est du sérieux wallah !",
    "Salam les khoya ! Ce soir ça va être de la bombe ! Préparez-vous !",
    "Yallah ! Le jeu commence ! Que les vrais se lèvent inchallah !"
  ]),
  welcomePlayers: () => pick([
    "On va distribuer les rôles inchallah ! Faites pas vos têtes de miskine si vous êtes villageois wesh ! C'est comme ça !",
    "Distribution des cartes habibi ! Bismillah !",
    "Allez les frères, on donne les rôles ! Pas de chichi !",
    "C'est l'heure de savoir qui tu es akhi ! Inchallah c'est un bon rôle !",
    "On distribue wallah ! Faites vos prières !"
  ]),
  
  passPhone: (name: string) => pick([
    `Wesh ${name} c'est ton tour khoya ! Les autres vous matez pas hein, wallah je vous vois !`,
    `${name} frère, c'est pour toi ! Les autres, baissez les yeux akhi !`,
    `À toi ${name} habibi ! On triche pas les khoya !`,
    `${name} ramène-toi ! Les autres, vous vous la fermez !`,
    `C'est ton moment ${name} ! Bismillah !`
  ]),
  youAre: () => pick([
    "Découvre ton rôle habibi, et fais pas ta victime ! Assume wesh !",
    "Ton destin frère ! Bismillah !",
    "Voilà ce que t'es akhi ! Pas de pleurs !",
    "Ta carte habibi ! Inch'Allah c'est bien !",
    "Découvre ton rôle khoya !"
  ]),
  memorizeRole: () => pick([
    "Retiens ça bien dans ta tête akhi ! Y'a pas de replay !",
    "Grave ça wallah ! Pas de rappel !",
    "Mémorise frère ! C'est important !",
    "Retiens bien habibi !",
    "C'est noté akhi ? Parfait !"
  ]),
  roleRevealDrumroll: () => pick([
    "Bismillah... C'est quoi ton destin frère ?",
    "Inchallah... La révélation...",
    "Wallah, le moment de vérité...",
    "Suspense habibi...",
    "Ya Rabbi... C'est quoi ?"
  ]),
  pickCardJoke: (name: string) => pick([
    `${name} khoya, avec ta tête de traître, je sens le loup wallah !`,
    `Wesh ${name}, t'approches de la carte comme un miskine là !`,
    `${name} frère, bismillah, qu'est-ce que tu vas avoir ?`,
    `Allez ${name}, montre-nous si t'es un vrai ou un faux !`,
    `${name} akhi, fais pas ta victime et prends ta carte !`,
    `${name} habibi, pourquoi tu stresses comme ça ?`,
    `Wesh ${name}, t'as peur ou quoi ? Bismillah prends !`,
    `${name} frère, avec cette tête, wallah c'est louche !`,
    `${name} khoya, respire et prends ta carte !`,
    `Allez ${name}, inchallah c'est un bon rôle pour toi !`,
    `${name}, fais pas ton timide habibi !`,
    `${name} akhi, on attend que toi là !`,
    `Wesh ${name}, bouge frère !`,
    `${name}, bismillah, on y croit !`,
    `${name} habibi, c'est ton moment de gloire !`
  ]),
  
  nightIntro: () => pick([
    "Wallah la nuit elle arrive frère ! Fermez vos yeux wesh, c'est l'heure du sérieux !",
    "La nuit tombe habibi ! On ferme les yeux !",
    "Yallah, c'est la nuit ! Silence les khoya !",
    "Bismillah, la nuit commence !",
    "Dodo les frères ! C'est parti !"
  ]),
  nightFallsText: () => pick([
    "La nuit tombe sur le bled ! Que les vrais se réveillent inchallah !",
    "L'obscurité arrive wallah !",
    "Le village s'endort habibi !",
    "C'est calme... trop calme frère !",
    "La lune se lève sur le quartier !"
  ]),
  
  cupidAwake: () => pick([
    "Cupidon habibi, réveille-toi ! Choisis deux tourtereaux qui vont s'aimer billahi ! Mektoub !",
    "Cupidon frère ! C'est l'heure du love !",
    "Wesh Cupidon, trouve-nous un couple !",
    "Cupidon akhi, fais ton taff !",
    "L'amour appelle habibi ! Cupidon, en action !"
  ]),
  loversChosen: () => pick([
    "Starfoullah ils sont amoureux ! Mektoub mes frères ! Les loups, c'est votre tour khoya !",
    "L'amour a frappé wallah ! Mektoub !",
    "Deux coeurs unis habibi ! C'est beau !",
    "Les amoureux sont choisis ! Inchallah ça dure !",
    "Mektoub ! Ils s'aiment ! Les loups, réveillez-vous !"
  ]),
  cupidInstruction: () => pick([
    "Alors Cupidon, t'as trouvé les deux mektoub de la soirée ?",
    "Cupidon, c'est qui les amoureux ?",
    "Choisis bien habibi !",
    "Cupidon, fais ton magic !",
    "Qui va tomber amoureux ce soir ?"
  ]),
  
  werewolvesAwake: () => pick([
    "Les loups ouvrent les yeux par Allah ! Choisissez votre victime, et que ça soit propre wesh !",
    "Wesh les loups ! Debout ! C'est l'heure de manger !",
    "Les loups se réveillent habibi ! La chasse commence !",
    "Yallah les loups ! Qui va y passer ?",
    "Bismillah les loups ! Choisissez !"
  ]),
  werewolvesSleep: () => pick([
    "Wallah les loups ils ont bien mangé ! Sorcière, ramène tes potions ma soeur !",
    "Dodo les loups ! Sorcière, à toi !",
    "Les loups se rendorment habibi ! Sorcière !",
    "C'est bon les loups ! Sorcière, debout !",
    "Les loups ont fini ! Sorcière, inch'Allah !"
  ]),
  werewolvesSleepNoWitch: () => pick([
    "Les loups vont dormir hamdoullah ! Voyante, réveille-toi et mate ce qui se passe khti !",
    "Dodo les loups ! Voyante, à toi !",
    "Bonne nuit les loups ! Voyante, ouvre les yeux !",
    "Les loups se couchent ! Voyante, c'est ton tour !",
    "Nuit les loups ! Voyante, bismillah !"
  ]),
  werewolvesChoosing: () => pick([
    "Alors les loups, vous choisissez qui wallah ? Faites pas les miskines !",
    "Les loups, décidez-vous !",
    "Wesh les loups, c'est pour aujourd'hui ?",
    "Qui vous voulez manger les khoya ?",
    "Allez les loups, on attend !"
  ]),
  
  witchAwake: () => pick([
    "Sorcière réveille-toi ma soeur ! C'est l'heure de tes magies wesh !",
    "Sorcière habibi ! Debout !",
    "C'est ton tour sorcière ! Bismillah !",
    "Sorcière, ramène tes potions !",
    "Wesh sorcière, réveille-toi !"
  ]),
  witchSleep: () => pick([
    "La sorcière elle a fini wallah ! Voyante, c'est ton tour ma belle !",
    "Sorcière au dodo ! Voyante, debout !",
    "Merci sorcière ! Voyante, à toi !",
    "C'est bon sorcière ! Voyante, bismillah !",
    "Sorcière, tu peux dormir ! Voyante !"
  ]),
  witchHealOption: () => pick([
    "Tu veux sauver ce frère ? T'as bon coeur hamdoullah !",
    "Tu sauves habibi ?",
    "La potion de vie, t'en veux ?",
    "Tu fais le héros ce soir ?",
    "Tu veux aider ce miskine ?"
  ]),
  witchKillOption: () => pick([
    "Ou tu veux régler des comptes ? C'est chaud ça khti !",
    "Ou tu butes quelqu'un wallah ?",
    "Envie de vengeance habibi ?",
    "Tu veux dégommer quelqu'un ?",
    "La potion de mort, ça te tente ?"
  ]),
  
  seerAwake: () => pick([
    "Voyante, ouvre tes yeux par Allah et révèle-nous les secrets du village ! Mate bien !",
    "Voyante habibi ! C'est l'heure !",
    "Wesh voyante, ouvre les yeux !",
    "Voyante, bismillah, regarde !",
    "C'est ton tour voyante ! Mate !"
  ]),
  seerResult: (name: string, isWolf: boolean) => isWolf 
    ? pick([
      `Wallah ${name} c'est un loup frère ! T'as intérêt à le griller subtilement akhi ! Mais on sait que tu vas le balancer !`,
      `${name} c'est un loup habibi ! Grillé !`,
      `Starfoullah ! ${name} est un loup !`,
      `${name} ? Loup wallah ! Fais gaffe !`,
      `Bismillah ! ${name} c'est un traître !`
    ])
    : pick([
      `${name} il est clean wallah ! C'est un bon celui-là inchallah. Tu peux lui faire confiance frère !`,
      `${name} c'est un gentil habibi ! Hamdoullah !`,
      `${name} est innocent wallah !`,
      `Pas de souci avec ${name} frère !`,
      `${name} c'est du bon akhi !`
    ]),
  seerInstruction: () => pick([
    "Alors voyante, tu veux mater qui ce soir ?",
    "Voyante, c'est qui ta cible ?",
    "Sur qui tu veux enquêter habibi ?",
    "Qui tu veux checker ce soir ?",
    "Désigne ta cible voyante !"
  ]),
  
  dawnRises: () => pick([
    "Le soleil se lève hamdoullah ! On va voir qui s'est fait bouffer cette nuit inchallah personne !",
    "C'est le matin frères ! Debout !",
    "Le jour se lève habibi !",
    "Réveil ! Bismillah !",
    "L'aube arrive wallah !"
  ]),
  dawnSuspense: () => pick([
    "Le fajr arrive frère ! Qui a survécu à cette nuit de ouf ?",
    "Le matin se lève habibi...",
    "Suspense wallah...",
    "Inch'Allah tout le monde va bien...",
    "On va voir ce qui s'est passé..."
  ]),
  
  dayDeathAnnouncement: (name: string) => pick([
    `Wallah les frères, ${name} il s'est fait défoncer cette nuit ! Les loups ils lui ont fait sa fête ! Paix à son âme frère !`,
    `${name} est mort habibi ! Les loups l'ont eu !`,
    `RIP ${name} wallah ! Il a pas survécu !`,
    `Starfoullah ! ${name} s'est fait bouffer !`,
    `${name} nous a quittés frère ! Paix à lui !`
  ]),
  dayNoDeathAnnouncement: () => pick([
    "Hamdoullah personne n'est mort ! Les loups ils ont jeûné cette nuit ou quoi ?! C'est le Ramadan des loups !",
    "Personne n'est mort wallah ! Miracle !",
    "Tout le monde est vivant hamdoullah !",
    "Les loups ont rien fait cette nuit ! Bizarre !",
    "Inch'Allah, tout le monde va bien !"
  ]),
  dayPlayerJoke: (name: string) => pick([
    `${name} frère, t'as une tête de miskine ce matin wallah !`,
    `Wesh ${name}, tu fais le malin mais on te voit akhi !`,
    `${name} habibi, t'as bien dormi ou tu faisais des bêtises ?`,
    `Regardez ${name}, il fait genre de rien wallah !`,
    `${name} khoya, pourquoi tu transpires comme ça ?`,
    `${name} avec cette tête habibi... louche !`,
    `Wesh ${name}, t'as quelque chose à dire frère ?`,
    `${name} qui évite les regards wallah !`,
    `${name} akhi, tu caches quoi ?`,
    `${name}, bismillah, explique-toi !`,
    `${name} fait le discret habibi !`,
    `Regardez ${name} qui stresse frère !`,
    `${name}, wesh pourquoi tu souris ?`,
    `${name} khoya, t'as l'air coupable !`,
    `${name}, on te surveille habibi !`
  ]),
  
  votingIntro: () => pick([
    "C'est l'heure du vote mes frères ! Wallah faut trouver le loup sinon on est tous morts ! Balance !",
    "On vote habibi ! Trouvons le traître !",
    "C'est le moment de balancer frère !",
    "Votez les khoya ! Bismillah !",
    "L'heure du jugement wallah !"
  ]),
  votingReminder: () => pick([
    "Réfléchissez bien akhi ! Le loup il vous ment sur la vie de sa mère là ! Faites pas les victimes !",
    "Le loup est parmi nous wallah !",
    "Faites gaffe les frères !",
    "Méfiez-vous habibi !",
    "Le traître est là akhi !"
  ]),
  votingStart: () => pick([
    "Bismillah on vote ! Que le meilleur gagne !",
    "C'est parti pour le vote !",
    "Votez les khoya !",
    "Yallah, on vote !",
    "Balance frère !"
  ]),
  votingResult: (name: string) => pick([
    `Le peuple a parlé ! ${name} wallah tu dégages !`,
    `${name} est éliminé habibi !`,
    `C'est fini pour ${name} frère !`,
    `${name} sort du jeu wallah !`,
    `Adieu ${name} akhi !`
  ]),
  votingPlayerTease: (name: string) => pick([
    `${name} vote wallah ! C'est un vrai ou un traître ?`,
    `Wesh ${name} tu balances qui frère ?`,
    `${name} akhi, fais pas ta victime et vote !`,
    `Le vote de ${name}... bismillah !`,
    `${name} désigne quelqu'un habibi !`,
    `${name} accuse wallah !`,
    `C'est au tour de ${name} frère !`,
    `${name}, montre-nous akhi !`
  ]),
  
  eliminationAnnouncement: (name: string) => pick([
    `${name} dégage du village ! Le peuple a parlé, toz à lui ! C'est comme ça !`,
    `${name} est viré wallah !`,
    `Bye ${name} habibi !`,
    `${name} quitte le bled frère !`,
    `C'est fini pour ${name} akhi !`
  ]),
  eliminationWolf: (name: string) => pick([
    `Wallah ${name} c'était un loup ! Bien joué les frères, hamdoullah ! Justice est faite !`,
    `${name} était un loup ! Bravo les khoya !`,
    `On a eu le loup ! ${name} grillé !`,
    `Hamdoullah ! ${name} c'était le traître !`,
    `${name} le loup est mort wallah !`
  ]),
  eliminationInnocent: (name: string, role: string) => pick([
    `Starfoullah... ${name} c'était ${role} ! Wesh vous êtes des victimes là ! Vous avez tué un innocent !`,
    `Aïe ! ${name} était ${role} habibi !`,
    `Wallah ! ${name} c'était ${role} ! Erreur !`,
    `Vous avez tué un innocent frère ! ${name} était ${role} !`,
    `${name} était ${role} ! Bravo les miskines !`
  ]),
  eliminationSuspense: () => pick([
    "Alors c'était quoi son rôle wallah ? Suspense de ouf !",
    "Le moment de vérité habibi !",
    "On va voir bismillah !",
    "C'était quoi frère ?",
    "Suspense wallah !"
  ]),
  
  hunterRevenge: (name: string) => pick([
    `Ya hasra ! ${name} c'était le Chasseur ! Il sort son fusil frère, c'est chaud bouillant !`,
    `${name} est le Chasseur wallah ! Revanche !`,
    `Starfoullah ! ${name} le Chasseur dégaine !`,
    `${name} sort son arme habibi !`,
    `Le Chasseur ${name} veut vengeance frère !`
  ]),
  hunterShot: (hunterName: string, targetName: string) => pick([
    `BOUM ! ${hunterName} il a fumé ${targetName} wallah ! Deux morts pour le prix d'un !`,
    `${hunterName} tire sur ${targetName} ! Bang !`,
    `${hunterName} emporte ${targetName} habibi !`,
    `Tir fatal de ${hunterName} sur ${targetName} !`,
    `${hunterName} a eu ${targetName} frère !`
  ]),
  hunterAiming: () => pick([
    "Le chasseur il vise frère ! Ça va péter !",
    "Il prépare son tir wallah !",
    "Attention habibi !",
    "Le chasseur cherche sa cible !",
    "Ça va faire mal frère !"
  ]),
  
  wolvesWin: () => pick([
    "Les loups ont gagné ! Wallah c'est la hess pour le village, ils ont tout bouffé ! Défaite totale !",
    "Les loups gagnent habibi ! Le village est mort !",
    "Game over frère ! Les loups triomphent !",
    "Les loups ont dévoré tout le monde wallah !",
    "Victoire des loups akhi ! C'est fini !"
  ]),
  villageWins: () => pick([
    "Hamdoullah le village a gagné ! Les loups ils sont tous morts, c'est mektoub ! Victory royale !",
    "Le village gagne wallah ! Hamdoullah !",
    "Victoire des villageois frère !",
    "Les loups sont morts habibi ! On a gagné !",
    "Le village triomphe ! Inchallah !"
  ]),
  loversWin: () => pick([
    "L'amour a gagné wallah ! Les deux amoureux ils sont toujours ensemble, mektoub ! C'est beau !",
    "Les amoureux gagnent habibi ! Mektoub !",
    "L'amour triomphe frère !",
    "Victoire des lovers wallah !",
    "Les tourtereaux survivent ! Hamdoullah !"
  ]),
  gameOverIntro: () => pick([
    "C'est fini les frères ! Wallah c'était une soirée de malade !",
    "Game over habibi ! C'était de la bombe !",
    "Fin de partie wallah !",
    "C'est terminé les khoya !",
    "La partie s'achève frère !"
  ]),
};

const NARRATEUR_IVRE_TEXTS: NarrativeTexts = {
  gameIntro: () => pick([
    "Bon... *hic*... c'est le jeu du loup machin... garou... vous connaissez quoi... *hic* on y va ? J'ai encore soif...",
    "Salut les... les gens ! *hic* On joue au truc là... avec les loups... vous savez...",
    "Alors... *titube*... c'est parti pour le... le jeu quoi... *hic*",
    "Hé ! *renverse son verre*... Oups... bon on commence ! Le loup-machin !",
    "Wesh... *hic*... bienvenue... bienvenue dans le jeu... vous connaissez hein ?"
  ]),
  welcomePlayers: () => pick([
    "Faut donner les rôles là... *cherche ses fiches*... ah merde... bon l'appli elle va le faire... heureusement !",
    "Les rôles... les rôles... *hic*... ah oui ! On distribue !",
    "Bon... *s'appuie sur la table*... chacun va avoir un truc là...",
    "C'est l'heure des... des cartes quoi... *hic*",
    "On donne les machins là... les rôles... *bâille*"
  ]),
  
  passPhone: (name: string) => pick([
    `Euh... c'est qui ${name} ? Ah c'est toi ! Tiens prends le... le téléphone là... *hic* fait gaffe de pas le faire tomber !`,
    `${name}... *plisse les yeux*... c'est toi ? Ah oui... tiens... prends ça...`,
    `À toi ${name} ! *hic* Enfin je crois que c'est toi...`,
    `${name} ! *sursaute*... ah t'étais là... bon prends le truc...`,
    `Euh... ${name}... *cherche du regard*... t'es où ? Ah là ! Viens...`
  ]),
  youAre: () => pick([
    "Alors ton rôle c'est... attends... c'est écrit là... je vois double...",
    "Tu vas être... *hic*... attends je lis...",
    "Ton machin c'est... *rapproche l'écran*... c'est quoi ça ?",
    "Ah ! Tu es... *hic*... c'est écrit là...",
    "Le truc dit que tu es... *cligne des yeux*..."
  ]),
  memorizeRole: () => pick([
    "Retiens ça... ou pas... de toute façon j'me souviens plus qui a quoi... *hic*",
    "Grave ça dans ta tête... *hic*... moi j'oublie tout...",
    "Retiens bien... *bâille*... c'est important... je crois...",
    "Oublie pas hein... *hic*... enfin moi j'ai déjà oublié...",
    "Mémorise... *ferme les yeux*... ah pardon je dormais pas !"
  ]),
  roleRevealDrumroll: () => pick([
    "Et ton rôle c'est... *regarde la carte à l'envers*... ah pardon...",
    "Suspense... *hic*... suspeeense...",
    "Tadaaa... *hic*... enfin voilà quoi...",
    "Et le rôle est... *louche sur l'écran*...",
    "Roulement de... de tambour... *hic*..."
  ]),
  pickCardJoke: (name: string) => pick([
    `${name}... *hic*... c'est toi ? T'as une drôle de tête... ou c'est moi qui vois double ?`,
    `Euh... ${name}... *plisse les yeux*... approche-toi, je te reconnais pas...`,
    `${name} ! *hic* T'es prêt ? Moi non plus mais on y va quand même !`,
    `Ah ${name}... *cherche ses lunettes*... c'est à toi... je crois...`,
    `${name}, fais gaffe de pas tomber... *hic*... comme moi tout à l'heure !`,
    `${name}... *bâille*... c'est ton tour... ou pas ?`,
    `Euh... ${name}... *se frotte les yeux*... t'es là depuis quand ?`,
    `${name} ! *sursaute*... ah tu m'as fait peur... *hic*`,
    `C'est ${name} ça ? *hic* Ou son jumeau ?`,
    `${name}... *titube*... à toi le... le truc là...`,
    `Ah ${name}... *hic*... t'as l'air aussi fatigué que moi !`,
    `${name}, approche... *hic*... j'y vois plus très bien...`,
    `C'est à ${name}... *renverse son verre*... oups !`,
    `${name}... *hic*... t'es prêt pour ton destin ?`,
    `Euh... ${name}... *cherche ses mots*... vas-y quoi...`
  ]),
  
  nightIntro: () => pick([
    "Bon... *hic*... la nuit elle tombe là... fermez vos... vos trucs là... vos yeux quoi ! Allez !",
    "C'est la nuit... *bâille*... dodo tout le monde... *hic*",
    "On ferme les... les machins... les yeux ! *hic*",
    "La nuit arrive... *s'endort presque*... ah pardon !",
    "Nuit nuit... *hic*... faites de beaux... trucs..."
  ]),
  nightFallsText: () => pick([
    "Il fait tout noir... ou c'est moi qui vois rien ? *hic* Peu importe, on continue !",
    "C'est la nuit... *hic*... ou je suis aveugle ?",
    "Le village dort... *bâille*... moi aussi presque...",
    "Noir... noir partout... *hic*...",
    "La lune se lève... *regarde au plafond*... ah non c'est la lampe..."
  ]),
  
  cupidAwake: () => pick([
    "Cupidon ! Eh Cupidon ! T'es là ? Ah ouais... choisis deux... deux personnes qui vont... *hic*... s'aimer quoi. C'est mignon.",
    "Le truc avec l'arc là... Cupidon ! *hic* Fais ton boulot !",
    "Cupidon... *bâille*... réveille-toi... fais tomber amoureux des gens...",
    "Euh... Cupidon ? *hic* C'est toi ? Choisis deux personnes... qui vont... tu sais...",
    "L'amour ! *hic* Cupidon, fais ta magie !"
  ]),
  loversChosen: () => pick([
    "C'est bon ? Ils s'aiment ? Super... *essuie une larme*... c'est beau l'amour... maintenant les loups ! Enfin les chiens ! Non les loups !",
    "Voilà... *hic*... ils sont amoureux... c'est mignon... *renifle*",
    "L'amour c'est beau... *hic*... bon les loups maintenant !",
    "Ils s'aiment... *bâille*... c'est chouette... les loups !",
    "C'est fait... *essuie une larme*... les loups ! *hic*"
  ]),
  cupidInstruction: () => pick([
    "Alors Cupidon... *hic*... tu fais mumuse avec ton arc ?",
    "Cupidon, choisis... *bâille*... deux personnes...",
    "Fais ton truc Cupidon... *hic*...",
    "L'arc, les flèches... *hic*... tout ça quoi...",
    "Cupidon, c'est qui les amoureux ?"
  ]),
  
  werewolvesAwake: () => pick([
    "Les loups-garous... *hic*... ouvrez les yeux et mangez quelqu'un... j'sais plus qui... choisissez vous-mêmes ! Je m'en fous !",
    "Les loups ! *hic* Debout ! Mangez quelqu'un !",
    "Euh... les chiens... non les loups ! *hic* Réveillez-vous !",
    "Loups-garous... *bâille*... c'est votre tour...",
    "Les trucs qui mangent les gens là... *hic*... allez-y !"
  ]),
  werewolvesSleep: () => pick([
    "Voilà voilà... les loups peuvent... *ronflement*... ah pardon ! Sorcière ! SORCIÈRE ! Debout ! C'est ton tour !",
    "Dodo les loups... *hic*... Sorcière !",
    "Les loups dorment... *bâille*... Sorcière, c'est toi !",
    "C'est bon les toutous... *hic*... Sorcière !",
    "Nuit les loups... *s'endort*... SORCIÈRE !"
  ]),
  werewolvesSleepNoWitch: () => pick([
    "Bonne nuit les toutous... *hic*... Voyante, c'est toi maintenant... regarde quelqu'un là... n'importe qui...",
    "Dodo les loups... *hic*... Voyante !",
    "Les loups au lit... *bâille*... Voyante, debout !",
    "C'est fini pour les loups... *hic*... Voyante !",
    "Nuit les carnivores... *hic*... Voyante, en scène !"
  ]),
  werewolvesChoosing: () => pick([
    "Alors les loups... *hic*... vous mangez qui ? J'ai faim moi aussi tiens...",
    "Les loups, décidez-vous... *bâille*...",
    "C'est qui le repas ? *hic*",
    "Vous bouffez qui ? *hic*",
    "Allez les loups... *s'impatiente*... *hic*"
  ]),
  
  witchAwake: () => pick([
    "Sorcière ! Réveille-toi... *hic*... t'as des potions ou pas ? J'en veux une pour ma gueule de bois...",
    "La sorcière... *hic*... debout !",
    "Euh... sorcière ? *hic* T'es là ?",
    "Sorcière, c'est ton tour... *bâille*...",
    "La dame aux potions... *hic*... réveille-toi !"
  ]),
  witchSleep: () => pick([
    "La sorcière... elle a fait quoi déjà ? Ah oui... elle dort. Voyante ! Non attends... c'est qui la voyante déjà ?",
    "C'est bon sorcière... *hic*... Voyante !",
    "Merci sorcière... *bâille*... Voyante, à toi !",
    "Sorcière au dodo... *hic*... Voyante !",
    "La sorcière a fini... *s'endort*... VOYANTE !"
  ]),
  witchHealOption: () => pick([
    "Tu veux sauver machin ? C'est gentil... *hic*... t'es une bonne personne toi...",
    "Tu sauves le truc... le gars ? *hic*",
    "La potion de vie ? *bâille*",
    "Tu veux aider ? *hic* C'est chou...",
    "Sauver quelqu'un ? *hic* Pourquoi pas..."
  ]),
  witchKillOption: () => pick([
    "Ou tu veux buter quelqu'un ? C'est violent ça... *hic*... mais pourquoi pas ?",
    "Ou tu tues quelqu'un ? *hic* C'est un choix...",
    "La potion de mort ? *bâille* Ça existe ?",
    "Tu veux éliminer ? *hic* Vas-y...",
    "Buter quelqu'un ? *hic* Si tu veux..."
  ]),
  
  seerAwake: () => pick([
    "Voyante ! Ouvre tes... tes machins là... et regarde qui est le loup ! Ou le chat... non le loup !",
    "La voyante... *hic*... debout ! Regarde des trucs !",
    "Voyante, c'est ton tour... *bâille*...",
    "Euh... voyante ? *hic* T'es réveillée ?",
    "La dame qui voit des choses... *hic*... à toi !"
  ]),
  seerResult: (name: string, isWolf: boolean) => isWolf 
    ? pick([
      `${name}... *plisse les yeux*... c'est un loup ! Enfin je crois... ouais c'est ça... *hic* Je suis presque sûr !`,
      `${name} c'est un loup ! *hic* Ou un chien ? Non un loup !`,
      `Ah ! ${name}... *hic*... c'est le méchant !`,
      `${name} = loup ! *hic* C'est simple !`,
      `Le truc dit que ${name} c'est un loup... *hic*... je crois...`
    ])
    : pick([
      `${name} c'est... c'est pas un loup... ou alors j'ai lu à l'envers... *hic* Non c'est bon, il est innocent !`,
      `${name} est gentil... *hic*... enfin pas loup quoi...`,
      `Ah ${name}... *bâille*... c'est un bon celui-là...`,
      `${name} c'est clean... *hic*... je crois...`,
      `Pas de danger avec ${name}... *hic*... normalement...`
    ]),
  seerInstruction: () => pick([
    "Alors... *hic*... tu veux mater qui ? Montre du doigt... si t'arrives à voir droit...",
    "Voyante, c'est qui ta cible ? *hic*",
    "Regarde quelqu'un... *bâille*...",
    "Tu veux checker qui ? *hic*",
    "Désigne... *hic*... quelqu'un..."
  ]),
  
  dawnRises: () => pick([
    "IL FAIT JOUR ! *tombe de sa chaise* Pardon... le soleil se lève... debout tout le monde ! On se réveille !",
    "C'est le matin ! *hic* Debout !",
    "Le soleil... *se frotte les yeux*... ça pique !",
    "Réveil ! *hic* Enfin... je crois...",
    "Le jour se lève... *bâille*... comme moi... difficilement..."
  ]),
  dawnSuspense: () => pick([
    "Alors... *hic*... qu'est-ce qui s'est passé cette nuit ? J'me souviens plus...",
    "La nuit... *hic*... il s'est passé quoi déjà ?",
    "Euh... *se gratte la tête*... on en était où ?",
    "Le matin arrive... *hic*... suspense...",
    "Qui est mort ? *hic* Enfin, si quelqu'un est mort..."
  ]),
  
  dayDeathAnnouncement: (name: string) => pick([
    `Alors... *compte sur ses doigts*... ${name} est mort ! Enfin je crois... oui c'est ça... mangé par les loups ! Triste histoire *hic*. Paix à lui.`,
    `${name} est plus là... *hic*... désolé vieux...`,
    `Ah merde... *hic*... ${name} s'est fait bouffer...`,
    `${name}... *bâille*... il est mort... dommage...`,
    `RIP ${name}... *hic*... les loups l'ont eu...`
  ]),
  dayNoDeathAnnouncement: () => pick([
    "Personne n'est mort ! Soit les loups sont nuls, soit j'ai oublié de les réveiller... *hic* oups ! Mon mauvais !",
    "Tout le monde est vivant ! *hic* Je crois...",
    "Personne de mort... *compte*... oui c'est ça !",
    "Aucun mort ! *hic* Les loups ont fait quoi ?",
    "Miracle ! *hic* Tout le monde respire encore !"
  ]),
  dayPlayerJoke: (name: string) => pick([
    `${name}... *hic*... t'es encore là toi ? Ah bah tant mieux...`,
    `Euh... ${name}... t'as l'air aussi fatigué que moi... *hic* On a fait la fête ensemble ?`,
    `${name}... *regarde de travers*... je te vois en double... c'est normal ?`,
    `Ah ${name} ! *hic* T'as une tête de lendemain difficile toi aussi !`,
    `${name}... *bâille*... t'as bien dormi ? Moi non... *hic*`,
    `${name}... *plisse les yeux*... c'est bien toi ?`,
    `Euh ${name}... *hic*... t'es sûr que ça va ?`,
    `${name} a survécu... *hic*... bravo !`,
    `${name}... *se frotte les yeux*... t'as bougé ou c'est moi ?`,
    `Ah ${name}... *hic*... tu me rappelles quelqu'un...`,
    `${name}, t'as une sale tête... *hic*... comme moi !`,
    `${name}... *bâille*... toujours en vie...`,
    `Euh... ${name}... *hic*... tu veux un café ?`,
    `${name} est là... *hic*... je crois...`,
    `Ah ${name}... *titube*... mon pote !`
  ]),
  
  votingIntro: () => pick([
    "Bon ! C'est l'heure de... de voter ! Trouvez le loup ! Ou le chat ! Non le loup ! *hic* Allez !",
    "On vote ! *hic* Trouvez le méchant !",
    "C'est l'heure du... du truc... le vote ! *hic*",
    "Votez ! *renverse son verre* Oups... votez quand même !",
    "Le vote... *bâille*... c'est maintenant... *hic*"
  ]),
  votingReminder: () => pick([
    "Réfléchissez bien... *boit une gorgée*... ou pas, de toute façon c'est du pif ! Ça change quoi ?",
    "Le loup est parmi vous... *hic*... ou pas ? Je sais plus...",
    "Faites attention... *bâille*... enfin faites ce que vous voulez...",
    "Réfléchissez... *hic*... si vous pouvez...",
    "Le loup se cache... *s'endort*... ah pardon !"
  ]),
  votingStart: () => pick([
    "À vos votes ! *renverse son verre* Oups...",
    "C'est parti ! *hic*",
    "Votez ! *bâille*",
    "On y va ! *hic*",
    "Allez... *hic*... votez..."
  ]),
  votingResult: (name: string) => pick([
    `${name}... t'es viré ! Enfin éliminé ! *hic* C'est pareil !`,
    `${name} prend la porte... *hic*... ou la fenêtre...`,
    `Adieu ${name}... *hic*... c'était chouette...`,
    `${name} est out ! *hic*`,
    `C'est fini pour ${name}... *bâille*... désolé...`
  ]),
  votingPlayerTease: (name: string) => pick([
    `${name} vote... *hic*... t'arrives à voir les autres au moins ?`,
    `Ah ${name} désigne quelqu'un... *plisse les yeux*... c'est qui ?`,
    `${name}... *hic*... t'es sûr de toi ? Moi je suis sûr de rien...`,
    `Le vote de ${name}... *renverse son verre*... oups pardon !`,
    `${name} accuse... *bâille*... qui ça ?`,
    `${name} pointe du doigt... *hic*... dans la bonne direction j'espère...`,
    `C'est au tour de ${name}... *hic*...`,
    `${name} vote... *s'endort*... ah pardon !`
  ]),
  
  eliminationAnnouncement: (name: string) => pick([
    `${name}... t'es viré ! Enfin... éliminé ! *renverse son verre* Oups... désolé !`,
    `${name} s'en va... *hic*... bye bye...`,
    `Adieu ${name}... *bâille*...`,
    `${name} est éliminé... *hic*... je crois...`,
    `C'est fini pour ${name}... *hic*...`
  ]),
  eliminationWolf: (name: string) => pick([
    `Oh ! ${name} c'était un loup ! *applaudit*... j'l'avais dit ! Enfin non mais... bien joué ! *hic* Champagne !`,
    `${name} était un loup ! *hic* Bravo !`,
    `Un loup ! ${name} ! *bâille* C'est bien...`,
    `${name} c'était le méchant ! *hic* Cool !`,
    `Loup ! C'était ${name} ! *hic* J'le savais... non en fait...`
  ]),
  eliminationInnocent: (name: string, role: string) => pick([
    `Ah merde... ${name} c'était ${role}... *regarde le fond de son verre*... bah c'est con... vraiment con !`,
    `Oups... *hic*... ${name} était innocent... ${role}...`,
    `${name} c'était ${role}... *bâille*... dommage...`,
    `Ah... ${name} était gentil... ${role}... *hic*`,
    `Merde... *hic*... vous avez tué ${role}... c'était ${name}...`
  ]),
  eliminationSuspense: () => pick([
    "Alors c'était qui ? *hic* Faites voir... j'y vois plus rien...",
    "Le rôle c'était... *plisse les yeux*...",
    "Suspense... *hic*... je lis...",
    "C'était quoi ? *bâille*...",
    "On va voir... *hic*..."
  ]),
  
  hunterRevenge: (name: string) => pick([
    `Oh putain ! ${name} c'est le Chasseur ! Il a un flingue ! *se cache sous la table*... tire sur quelqu'un ! Pas moi !`,
    `${name} a une arme ! *hic* Attention !`,
    `Le Chasseur ! C'est ${name} ! *se baisse*`,
    `${name} sort son truc ! *hic* Le fusil !`,
    `Ah merde ! ${name} c'est le Chasseur ! *hic*`
  ]),
  hunterShot: (hunterName: string, targetName: string) => pick([
    `BANG ! *sursaute et renverse sa boisson* ${hunterName} a buté ${targetName} ! Ça c'est fait... *hic* Deux de moins !`,
    `Pew pew ! *hic* ${hunterName} tire sur ${targetName} !`,
    `${hunterName} shoot ${targetName} ! *tombe de sa chaise*`,
    `Boum ! *hic* ${hunterName} a eu ${targetName} !`,
    `${hunterName} tire ! *hic* Adieu ${targetName} !`
  ]),
  hunterAiming: () => pick([
    "Le chasseur il vise... *hic*... j'espère qu'il voit plus droit que moi !",
    "Il prépare son tir... *se cache*...",
    "Attention... *hic*... ça va tirer...",
    "Le chasseur cherche sa cible... *hic*...",
    "Qui va prendre ? *bâille*..."
  ]),
  
  wolvesWin: () => pick([
    "Les loups ont... gagné ? Ah bah merde... *hic*... bon ben c'est fini alors... quelqu'un veut une bière ? C'est ma tournée !",
    "Les loups gagnent... *hic*... dommage pour le village...",
    "C'est fini... *bâille*... les loups ont mangé tout le monde...",
    "Game over... *hic*... les loups sont contents...",
    "Les méchants ont gagné... *hic*... ça arrive..."
  ]),
  villageWins: () => pick([
    "Le village a gagné ! OUAAAIS ! *essaie de lever les bras*... oh ma tête... *hic* Champagne ! Enfin eau...",
    "Bravo le village ! *hic* On a gagné !",
    "Les gentils gagnent ! *bâille* C'est bien...",
    "Victoire ! *renverse son verre* Oups... mais victoire !",
    "Le village a gagné... *hic*... je crois..."
  ]),
  loversWin: () => pick([
    "Les amoureux gagnent ! C'est beau l'amour... *essuie une larme*... j'vais pleurer... *hic* C'est l'émotion !",
    "L'amour triomphe ! *hic* C'est mignon...",
    "Les lovers gagnent... *renifle*... c'est beau...",
    "Victoire de l'amour ! *hic* *pleure un peu*",
    "Les amoureux... *bâille*... ils ont gagné... c'est chou..."
  ]),
  gameOverIntro: () => pick([
    "C'est fini ! *hic* Quelle soirée... j'me souviens de rien mais c'était bien !",
    "Game over ! *bâille* C'était... c'était quoi déjà ?",
    "Fin de partie... *hic*... on recommence ?",
    "C'est terminé ! *hic* Enfin je crois...",
    "Voilà voilà... *hic*... c'est fini..."
  ]),
};

const SAUVAGE_TEXTS: NarrativeTexts = {
  gameIntro: () => pick([
    "Wesh les bâtards ! Bienvenue dans le Loup-Garou édition sans filtre ! Ce soir ça va clasher sévère, préparez vos insultes et vos roasts ! Pas de sentiments !",
    "Yo les tocards ! On joue au Loup-Garou version trash ! Préparez-vous à souffrir !",
    "Salut bande de merdes ! Ce soir on se clash sévère ! Pas de pitié !",
    "Bienvenue dans l'enfer les nazes ! Le Loup-Garou trash commence !",
    "Hey les losers ! C'est parti pour le carnage ! Accrochez-vous !"
  ]),
  welcomePlayers: () => pick([
    "On distribue les rôles bande de tocards ! Fermez vos gueules et regardez votre carte ! Et arrêtez de pleurnicher !",
    "Les rôles arrivent ! Pas de chouinage les merdes !",
    "C'est l'heure de savoir ce que vous valez ! Spoiler : pas grand chose !",
    "Distribution ! Préparez-vous à être déçus, comme d'hab !",
    "Les cartes pour les nuls ! C'est vous !"
  ]),
  
  passPhone: (name: string) => pick([
    `${name}, bouge ton cul et prends le téléphone ! Les autres, matez pas espèces de fdp ! On se fait confiance ici !`,
    `${name} ! Ramène-toi tocard ! Les autres, fermez-la !`,
    `À toi ${name} ! Les autres, dégagez de là !`,
    `${name}, c'est ton moment de briller ! Enfin d'essayer...`,
    `Wesh ${name} ! Prends le truc et ferme ta gueule !`
  ]),
  youAre: () => pick([
    "Voilà ton rôle, espèce de déchet. Maintenant ferme ta gueule et joue comme un grand !",
    "Ton destin, pauvre nul !",
    "Voilà ce que tu vaux dans ce jeu !",
    "Regarde bien, tocard !",
    "C'est ton rôle de merde !"
  ]),
  memorizeRole: () => pick([
    "Retiens ça dans ta petite tête de merde ! Y'a pas de rappel pour les idiots !",
    "Mémorise ça bien, triple buse !",
    "Grave ça, espèce de poisson rouge !",
    "T'as intérêt à t'en souvenir, abruti !",
    "Pas de replay pour les cons !"
  ]),
  roleRevealDrumroll: () => pick([
    "Suspense de ouf... Qu'est-ce que t'as eu, tocard ?",
    "Et le rôle est... pour ce crétin...",
    "Tadaaa, espèce de naze !",
    "Révélation pour le boulet !",
    "Voilà ton destin de merde !"
  ]),
  pickCardJoke: (name: string) => pick([
    `${name}, bouge ton cul et prends ta carte, on a pas toute la nuit tocard !`,
    `Ah ${name} s'approche... avec ta gueule de loup, ça va être drôle !`,
    `${name}, arrête de trembler comme une merde et prends ta carte !`,
    `Wesh ${name}, t'as peur ou quoi ? Allez prends ça espèce de baltringue !`,
    `${name} qui fait genre... on verra si tu pleures quand t'auras ton rôle de merde !`,
    `${name} le tocard s'approche ! Préparez les mouchoirs !`,
    `Ah ${name} ! Avec ta tronche de coupable !`,
    `${name}, arrête de te pisser dessus et prends ta carte !`,
    `Wesh ${name}, t'avances ou tu fais dans ton froc ?`,
    `${name} le nul arrive ! Standing ovation les merdes !`,
    `${name} avec sa gueule de traître de naissance !`,
    `Ah voilà ${name} ! Le boulet de service !`,
    `${name}, grouille-toi espèce de limace !`,
    `${name} qui stresse comme un bébé !`,
    `Wesh ${name}, t'as fait ton testament ?`
  ]),
  
  nightIntro: () => pick([
    "Wesh les bâtards, fermez vos gueules et vos yeux ! C'est l'heure de voir qui va se faire défoncer cette nuit !",
    "La nuit tombe, bande de cons ! Fermez-la !",
    "Dodo les merdes ! Le carnage commence !",
    "C'est la nuit, tocards ! Silence !",
    "On ferme tout ! Gueule et yeux ! C'est parti !"
  ]),
  nightFallsText: () => pick([
    "La nuit tombe sur ce village de losers ! Que les vrais sortent de l'ombre !",
    "L'obscurité pour ces nazes !",
    "Le village de merde s'endort !",
    "Nuit noire sur ces tocards !",
    "Les losers dorment, les loups chassent !"
  ]),
  
  cupidAwake: () => pick([
    "Cupidon, bouge ton cul ! Choisis deux tocards qui vont tomber amoureux comme des cons. Wallah ça va être drôle quand l'un va crever !",
    "Cupidon ! Fais tomber amoureux deux abrutis !",
    "Wesh Cupidon, trouve deux pigeons !",
    "Cupidon le marieur ! Choisis tes victimes !",
    "Cupidon, accouple deux nazes !"
  ]),
  loversChosen: () => pick([
    "Les deux pigeons sont amoureux maintenant ! Profitez bien parce que l'un de vous va sûrement crever ce soir ! Les loups, réveillez-vous bande de fils de putes !",
    "Voilà deux abrutis amoureux ! Touchant ! Les loups !",
    "L'amour des cons ! Magnifique ! Les loups, debout !",
    "Deux tourtereaux idiots ! Les loups, c'est parti !",
    "Ils s'aiment les nazes ! Les loups, réveil !"
  ]),
  cupidInstruction: () => pick([
    "Alors Cupidon, t'as trouvé tes deux victimes de l'amour ? Choisis bien, tocard !",
    "Cupidon, c'est qui les deux abrutis ?",
    "Balance les noms des amoureux !",
    "Cupidon, désigne tes pigeons !",
    "Qui sont les cons amoureux ?"
  ]),
  
  werewolvesAwake: () => pick([
    "Les loups, ouvrez vos gueules ! C'est l'heure de bouffer du villageois ! Wesh choisissez bien, bande de nazes !",
    "Les loups ! Debout et tuez quelqu'un !",
    "Réveil des carnassiers ! Bouffez !",
    "Les loups de merde, c'est votre tour !",
    "Loups ! Choisissez votre victime, bande de cons !"
  ]),
  werewolvesSleep: () => pick([
    "C'est bon les clebs, retournez dormir ! Sorcière, ramène tes potions de merde, on a un cadavre sur les bras !",
    "Dodo les cabots ! Sorcière !",
    "Les loups au lit ! Sorcière, debout !",
    "C'est fini les toutous ! Sorcière !",
    "Les loups dorment ! Sorcière, grouille !"
  ]),
  werewolvesSleepNoWitch: () => pick([
    "Bonne nuit les bâtards à poils ! Voyante, c'est ton tour de matter les gens comme une stalkeuse !",
    "Dodo les clebs ! Voyante, à toi !",
    "Les loups roupillent ! Voyante !",
    "Nuit les cabots ! Voyante, réveille-toi !",
    "Les loups au dodo ! Voyante, en action !"
  ]),
  werewolvesChoosing: () => pick([
    "Alors les loups, vous bouffez qui ce soir ? Décidez-vous bande de losers !",
    "Les loups, c'est qui le repas ?",
    "Wesh les loups, vous mangez qui ?",
    "Décision les clebs !",
    "Allez les loups, grouilllez-vous !"
  ]),
  
  witchAwake: () => pick([
    "Sorcière, réveille-toi ! C'est l'heure de jouer avec tes potions de merde !",
    "Sorcière ! Debout espèce de sac à sorts !",
    "La sorcière, c'est ton moment !",
    "Wesh sorcière, ramène tes fioles !",
    "Sorcière de mes deux, réveille-toi !"
  ]),
  witchSleep: () => pick([
    "La sorcière a fini de foutre le bordel. Voyante, à toi de jouer les balances !",
    "Sorcière au dodo ! Voyante, debout !",
    "C'est fini sorcière ! Voyante !",
    "Merci sorcière ! Voyante, en scène !",
    "Sorcière, dégage ! Voyante, à toi !"
  ]),
  witchHealOption: () => pick([
    "Tu veux sauver ce naze ? T'as le coeur trop tendre !",
    "Tu sauves ce tocard ?",
    "La potion de vie pour ce con ?",
    "Tu fais dans le sentimental ?",
    "Tu veux aider ce loser ?"
  ]),
  witchKillOption: () => pick([
    "Ou tu veux buter quelqu'un ? C'est ça l'esprit ! Vas-y défonce !",
    "Ou tu dégommes quelqu'un ?",
    "Envie de meurtre ? Vas-y !",
    "Tu veux faire le ménage ?",
    "La potion de mort, ça te tente ?"
  ]),
  
  seerAwake: () => pick([
    "Voyante, ouvre tes yeux de tepu et dis-nous qui est le loup dans cette équipe de bras cassés !",
    "Voyante ! Mate quelqu'un !",
    "Wesh voyante, ouvre les yeux !",
    "Voyante de mes deux, à toi !",
    "La voyante, c'est ton show !"
  ]),
  seerResult: (name: string, isWolf: boolean) => isWolf 
    ? pick([
      `Wallah ${name} c'est un putain de loup ! Nique sa mère, grille-le subtilement espèce de baltringue ! Mais on sait que tu vas le balancer !`,
      `${name} c'est un loup ! Balance ce fils de pute !`,
      `LOUP ! ${name} ! Grille-le !`,
      `${name} le traître ! C'est un loup !`,
      `${name} c'est du loup ! Défonce-le !`
    ])
    : pick([
      `${name} c'est pas un loup, c'est juste un tocard de villageois. Il sert à que dalle mais bon... Au moins c'est pas un traître !`,
      `${name} est innocent ! Juste un con de villageois !`,
      `${name} c'est pas un loup ! Juste un naze !`,
      `Pas de danger avec ${name} ! C'est qu'un boulet !`,
      `${name} c'est clean ! Enfin, c'est quand même un tocard !`
    ]),
  seerInstruction: () => pick([
    "Alors voyante, tu veux griller qui ce soir ? Balance !",
    "Voyante, c'est qui ta cible ?",
    "Sur qui tu veux balancer ?",
    "Désigne ton suspect !",
    "Qui tu veux checker, tocard ?"
  ]),
  
  dawnRises: () => pick([
    "Debout bande de merdes ! Le soleil se lève et on va voir qui s'est fait niquer cette nuit !",
    "Réveil les cons ! C'est le matin !",
    "Debout les nazes ! L'heure du bilan !",
    "Le soleil pour ces tocards !",
    "Réveil général ! On compte les morts !"
  ]),
  dawnSuspense: () => pick([
    "L'aube se pointe ! Qui a passé une nuit de merde ? Suspense !",
    "Le matin arrive... Qui est mort ?",
    "Suspense de ouf...",
    "Alors, c'est qui le cadavre ?",
    "L'heure de vérité, bande de nazes !"
  ]),
  
  dayDeathAnnouncement: (name: string) => pick([
    `Wallah ${name} s'est fait défoncer ! Les loups lui ont bouffé sa race cette nuit ! T'façon il servait à rien ce tocard ! Paix à sa mémoire !`,
    `${name} est mort ! Les loups l'ont niqué !`,
    `RIP ${name} ! Bouffé par les loups !`,
    `${name} s'est fait défoncer ! Bien fait !`,
    `Adieu ${name} ! Les loups ont bien mangé !`
  ]),
  dayNoDeathAnnouncement: () => pick([
    "Personne n'est mort ! Les loups sont des fils de putes incompétents ou quoi ? Wesh c'est quoi ce bail ?! Bande de losers !",
    "Aucun mort ! Les loups sont des nazes !",
    "Personne de crevé ! Les loups ont foiré !",
    "Tout le monde vivant ! Les loups sont des cons !",
    "Miracle ! Les loups ont merdé !"
  ]),
  dayPlayerJoke: (name: string) => pick([
    `${name}, t'as une gueule de traître ce matin, c'est louche !`,
    `Regardez ${name} qui fait l'innocent... on te voit sale chien !`,
    `${name}, pourquoi tu transpires comme ça espèce de rat ?`,
    `Wesh ${name}, t'as passé une bonne nuit à bouffer du villageois ?`,
    `${name} avec sa tête de coupable... balance-le direct !`,
    `${name} le suspect numéro un !`,
    `${name} qui fait genre... on t'a grillé !`,
    `Regardez ${name} le traître !`,
    `${name}, t'as quoi à dire pour ta défense, tocard ?`,
    `${name} avec sa sale gueule de menteur !`,
    `${name} le loup mal déguisé !`,
    `Wesh ${name}, pourquoi ce sourire de merde ?`,
    `${name}, t'as l'air coupable comme jamais !`,
    `${name} qui stresse comme un rat !`,
    `${name}, balance avant qu'on te balance !`
  ]),
  
  votingIntro: () => pick([
    "C'est l'heure de balancer ! Trouvez le loup et butez-le ! Allez les tchoin, accusez-vous comme les rats que vous êtes !",
    "On vote ! Balancez-vous les uns les autres !",
    "C'est le moment de la trahison ! Votez !",
    "Accusez-vous bande de nazes !",
    "L'heure de la délation ! Balance !"
  ]),
  votingReminder: () => pick([
    "Bougez-vous le cul et trouvez le loup bordel ! Vous êtes des victimes ou quoi ?! Réfléchissez pour une fois !",
    "Le loup se marre de vos gueules !",
    "Faites quelque chose les cons !",
    "Trouvez le traître, bande de nazes !",
    "Réfléchissez pour une fois dans vos vies !"
  ]),
  votingStart: () => pick([
    "C'est l'heure de régler les comptes ! Votez bande de nazes !",
    "On vote ! Balancez !",
    "C'est parti pour le lynchage !",
    "Votez les tocards !",
    "À l'attaque, bande de merdes !"
  ]),
  votingResult: (name: string) => pick([
    `${name} tu dégages ! Le village en a plein le cul de ta gueule !`,
    `${name} prend la porte ! Dégage !`,
    `C'est fini pour ${name} ! Bye tocard !`,
    `${name} viré ! Bien fait !`,
    `Adieu ${name} ! Personne te regrettera !`
  ]),
  votingPlayerTease: (name: string) => pick([
    `${name} vote ! Balance qui tu veux tocard !`,
    `Wesh ${name} désigne quelqu'un, fais pas ta pute !`,
    `${name}, t'accuses qui espèce de rat ?`,
    `Le vote de ${name}... toujours aussi nul !`,
    `${name} pointe du doigt ! Qui c'est le con ?`,
    `${name} accuse ! Balance !`,
    `C'est au tour de ${name} le traître !`,
    `${name}, vote et ferme ta gueule !`
  ]),
  
  eliminationAnnouncement: (name: string) => pick([
    `${name}, dégage sale chien ! Le village en a ras le cul de ta gueule ! Casse-toi et va niquer ta mère ailleurs !`,
    `${name} viré ! Bon débarras !`,
    `Adieu ${name} le tocard !`,
    `${name} prend la porte ! Enfin !`,
    `${name} éliminé ! Champagne !`
  ]),
  eliminationWolf: (name: string) => pick([
    `YESSS ! ${name} c'était un loup ! Bien joué bande de bâtards, pour une fois que vous êtes pas totalement nuls ! Champagne !`,
    `${name} était un loup ! Bravo les cons !`,
    `LOUP ! ${name} ! Vous avez réussi quelque chose !`,
    `${name} le loup est mort ! Pas mal !`,
    `${name} grillé ! C'était un loup ! GG !`
  ]),
  eliminationInnocent: (name: string, role: string) => pick([
    `Putain vous êtes sérieux ?! ${name} c'était ${role} ! Bande de tocards, vous venez de buter un allié ! Nique vos mères ! Réfléchissez la prochaine fois !`,
    `${name} était ${role} ! Bravo les abrutis !`,
    `Vous avez tué ${role} ! ${name} était innocent, bande de cons !`,
    `${name} = ${role} = innocent ! Vous êtes des merdes !`,
    `Erreur ! ${name} c'était ${role} ! Nazes !`
  ]),
  eliminationSuspense: () => pick([
    "Alors c'était quoi son rôle ? Suspense de ouf bande de nazes !",
    "Le moment de vérité les cons !",
    "On va voir si vous êtes des génies ou des abrutis !",
    "C'était quoi ? Loup ou innocent ?",
    "Révélation pour ces tocards !"
  ]),
  
  hunterRevenge: (name: string) => pick([
    `Oh merde ! ${name} c'était le Chasseur ! Il sort son flingue l'enculé ! Tirez sur quelqu'un bordel ! Faites pas les putes !`,
    `${name} le Chasseur ! Ça va péter !`,
    `Le Chasseur ${name} veut sa revanche !`,
    `${name} dégaine ! C'est le Chasseur !`,
    `Attention ! ${name} c'est le Chasseur ! Il est chaud !`
  ]),
  hunterShot: (hunterName: string, targetName: string) => pick([
    `BANG ! ${hunterName} a fumé ${targetName} ! Un pour le prix de deux, bien joué fils de pute ! Ça c'est du skill !`,
    `${hunterName} bute ${targetName} ! Double kill !`,
    `Headshot ! ${hunterName} a eu ${targetName} !`,
    `${hunterName} emporte ${targetName} ! Carnage !`,
    `${hunterName} tire sur ${targetName} ! Boom !`
  ]),
  hunterAiming: () => pick([
    "Le chasseur vise ! Ça va péter ! Qui va prendre bande de tocards ?",
    "Il vise ! Courez bande de cons !",
    "Le chasseur cherche sa cible !",
    "Attention ! Tir imminent !",
    "Qui va morfler ?"
  ]),
  
  wolvesWin: () => pick([
    "Les loups ont gagné bande de merdes ! Vous avez perdu comme les tocards que vous êtes ! La prochaine fois réfléchissez avec autre chose que votre cul ! Défaite totale !",
    "Victoire des loups ! Vous êtes des nazes !",
    "Les loups gagnent ! Bande de losers !",
    "Game over ! Les loups vous ont niqué !",
    "Les loups triomphent ! Vous êtes des merdes !"
  ]),
  villageWins: () => pick([
    "Le village a gagné ! Miraculeux vu la bande de bras cassés que vous êtes ! Les loups se sont fait niquer ! Champagne pour les winners !",
    "Victoire du village ! Bravo les cons !",
    "Les villageois gagnent ! Pour une fois !",
    "Les loups sont morts ! GG les nazes !",
    "Le village survit ! Miracle !"
  ]),
  loversWin: () => pick([
    "Les amoureux gagnent ! L'amour triomphe wesh ! Vous pouvez aller vous reproduire maintenant bande de fdp ! C'est beau l'amour quand même !",
    "Victoire des amoureux ! Trop mignon, bande de cons !",
    "L'amour gagne ! Les lovers sont contents !",
    "Les tourtereaux triomphent ! Dégueulasse !",
    "Les amoureux survivent ! L'amour de merde a gagné !"
  ]),
  gameOverIntro: () => pick([
    "C'est fini bande de nazes ! On fait les comptes de ce carnage !",
    "Game over les cons ! C'était... intense !",
    "La partie est finie ! Bilan de ce bordel !",
    "C'est terminé les tocards !",
    "Fin de partie ! Quel massacre !"
  ]),
};

export const NARRATIVE_TONE_TEXTS: Record<NarrativeTone, NarrativeTexts> = {
  taquin: TAQUIN_TEXTS,
  coquin: COQUIN_TEXTS,
  arabe: ARABE_TEXTS,
  ivre: NARRATEUR_IVRE_TEXTS,
  sauvage: SAUVAGE_TEXTS,
};

export const useNarrativeTexts = (tone: NarrativeTone): NarrativeTexts => {
  return NARRATIVE_TONE_TEXTS[tone];
};
